/**
 * One tab owns each background upload (#4844). Every tab's manager resumes
 * from the same browser storage, so without an owner two tabs would upload
 * the same file and, with session tokens rotating on resume, fence each
 * other back and forth. A Web Lock per upload, held while that tab works on
 * it, decides who: a tab that cannot get the lock leaves the upload (and its
 * copy) to the tab that has it. Without `navigator.locks` every claim is
 * granted, as before.
 */

/** The part of `navigator.locks` the manager uses (tests pass their own). */
export type UploadLockManager = {
  request: (
    name: string,
    options: { ifAvailable: true },
    callback: (lock: unknown) => Promise<unknown> | unknown
  ) => Promise<unknown>;
};

const LOCK_PREFIX = 'imai-background-upload:';

export function createUploadLocks(lockManager?: UploadLockManager | null) {
  const held = new Map<string, () => void>();
  // Claims still waiting on the lock manager; a release cancels them.
  const pending = new Map<string, { cancelled: boolean }>();
  const release = (uploadId: string) => {
    const claim = pending.get(uploadId);
    if (claim) claim.cancelled = true;
    pending.delete(uploadId);
    held.get(uploadId)?.();
    held.delete(uploadId);
  };
  return {
    /**
     * Take the upload for this tab and hold it until `release`. False when
     * another tab owns it (or it was released while being claimed).
     */
    claim(uploadId: string): Promise<boolean> {
      if (!lockManager || held.has(uploadId)) return Promise.resolve(true);
      const claim = { cancelled: false };
      pending.set(uploadId, claim);
      return new Promise<boolean>((resolve) => {
        lockManager
          .request(LOCK_PREFIX + uploadId, { ifAvailable: true }, (lock) => {
            if (pending.get(uploadId) === claim) pending.delete(uploadId);
            if (!lock || claim.cancelled) {
              resolve(false);
              return undefined;
            }
            // The lock lasts as long as this promise stays pending.
            return new Promise<void>((done) => {
              held.set(uploadId, done);
              resolve(true);
            });
          })
          // Locks unusable here (a sandboxed frame): behave as without them.
          .catch(() => resolve(!claim.cancelled));
      });
    },
    release,
    releaseAll() {
      for (const uploadId of [...pending.keys(), ...held.keys()])
        release(uploadId);
    },
  };
}

export type UploadLocks = ReturnType<typeof createUploadLocks>;

/** This browser's Web Locks, read at call time (absent during SSR). */
export function browserUploadLocks(): UploadLocks {
  const locks =
    typeof navigator === 'undefined'
      ? undefined
      : (navigator.locks as UploadLockManager | undefined);
  return createUploadLocks(locks ?? null);
}
